"use client";

import React from "react";
import { Button } from "../common/button";

export interface TrustIndicator {
  icon: React.ReactNode;
  text: string;
}

export interface HeroWithBadgeProps {
  badge?: {
    icon?: React.ReactNode;
    text: string;
  };
  title: string;
  subtitle: string;
  ctaText: string;
  onCtaClick?: () => void;
  priceCard?: {
    label: string;
    price: string;
    suffix?: string;
  };
  trustIndicators?: TrustIndicator[];
  bgGradient?: string;
}

export function HeroWithBadge({
  badge,
  title,
  subtitle,
  ctaText,
  onCtaClick,
  priceCard,
  trustIndicators,
  bgGradient = "from-primary-50 via-white to-secondary-50"
}: HeroWithBadgeProps) {
  return (
    <section className={`relative bg-gradient-to-br ${bgGradient} py-20 md:py-28 overflow-hidden`}>
      {/* Background decoration */}
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute -top-40 -right-40 w-80 h-80 bg-primary-100 rounded-full blur-3xl opacity-30"></div>
        <div className="absolute -bottom-40 -left-40 w-80 h-80 bg-secondary-100 rounded-full blur-3xl opacity-30"></div>
      </div>
      
      <div className="relative max-w-4xl mx-auto px-4 text-center">
        {/* Badge */}
        {badge && (
          <div className="inline-flex items-center gap-2 bg-white px-4 py-2 rounded-full shadow-sm mb-6">
            {badge.icon || <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>}
            <span className="text-sm font-medium text-gray-700">{badge.text}</span>
          </div>
        )}
        
        <h1 className="text-4xl md:text-6xl font-bold text-gray-900 mb-6">
          {title}
        </h1>
        <p className="text-xl md:text-2xl text-gray-700 mb-10 leading-relaxed max-w-3xl mx-auto">
          {subtitle}
        </p>
        
        {/* CTA with price card */}
        <div className="flex flex-col sm:flex-row gap-6 justify-center items-center">
          <Button variant="primary" size="lg" className="min-w-[200px] shadow-lg hover:shadow-xl" onClick={onCtaClick}>
            {ctaText}
          </Button>
          {priceCard && (
            <div className="bg-white px-6 py-3 rounded-xl shadow-md border border-gray-100">
              <p className="text-sm text-gray-600">{priceCard.label}</p>
              <p className="text-2xl font-bold text-primary-600">
                {priceCard.price}
                {priceCard.suffix && <span className="text-sm font-normal text-gray-600">{priceCard.suffix}</span>}
              </p>
            </div>
          )}
        </div>
        
        {/* Trust indicators */}
        {trustIndicators && trustIndicators.length > 0 && (
          <div className="mt-12 flex flex-wrap justify-center gap-8 text-sm text-gray-600">
            {trustIndicators.map((indicator, idx) => (
              <div key={idx} className="flex items-center gap-2">
                {indicator.icon}
                <span>{indicator.text}</span>
              </div>
            ))}
          </div>
        )}
      </div>
    </section>
  );
}